import React, { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import {
  BubbleButton,
  BubbleFormGroup,
  BubbleImageSelector,
  BubbleInput,
  BubbleSelect,
  BubbleTextArea,
} from '../BubbleUI';
import { createPortal } from 'react-dom';
import { mapBy } from '@go-shared/lib/functional';
import { BareEventTemplate, EventDisplayOption } from '@go-shared/types/eventTemplate';
import BubbleTypeaheadSimple from '../BubbleUI/BubbleTypeaheadSimple';
import { EVENT_DISPLAY_MAP, EVENT_DISPLAY_TYPES } from '@go-shared/lib/event';
import { Brand } from '@go-shared/types/brand';
import McmVisibilityTable from '../McmVisibilityTable';
import AudienceVisibilityTable from '../AudienceVisibilityTable';
import { Mcm } from '@go-shared/types/mcm';
import { Audience } from '@go-shared/types/audience';
import { Property } from '@go-shared/types/property';
import { DailyProgramCta } from '@go-shared/types/dailyProgram';
import { CallToActions } from './CallToActions';

interface EventTemplateFormDataBase {
  images: string[];
  title: string;
  description: string;
  defaultDuration: number;
  notNative: boolean;
  inventoried: boolean;
  initialInventory: number;
  segment?: string;
  levels: string[];
  attributes: string[];
  interactiveFeedDisplay: EventDisplayOption | undefined;
  isPrivate: boolean;
  crewOnly: boolean;
  simple: boolean;
  homecoming: boolean;
  allAboard: boolean;
  vip: boolean;
  priced: boolean;
  initialPrice: number;
  mcm?: Mcm;
  audience?: Audience;
  audienceZoneFilter?: string;
  callToActions: DailyProgramCta[];
}

export type EventTemplateFormData = EventTemplateFormDataBase & {
  _dirty: boolean;
  _errors: Partial<EventTemplateFormError>;
};

type EventTemplateFormError = { [K in keyof EventTemplateFormDataBase]: string };

function DEFAULT_FORM_DATA(event?: BareEventTemplate): EventTemplateFormData {
  return {
    images: event?.images ?? [],
    title: event?.title ?? '',
    description: event?.description ?? '',
    defaultDuration: event?.defaultDuration ?? 0,
    notNative: event?.notNative ?? false,
    inventoried: event?.inventoried ?? false,
    initialInventory: event?.initialInventory ?? 0,
    segment: event?.segment ?? undefined,
    levels: event?.levels ?? [],
    attributes: event?.attributes ?? [],
    interactiveFeedDisplay: event?.interactiveFeedDisplay,
    isPrivate: event?.isPrivate ?? false,
    crewOnly: event?.crewOnly ?? false,
    simple: event?.simple ?? false,
    homecoming: event?.homecoming ?? false,
    allAboard: event?.allAboard ?? false,
    vip: event?.vip ?? false,
    priced: event?.priced ?? false,
    initialPrice: event?.initialPrice ?? 0,
    mcm: event?.mcm,
    audience: event?.audience,
    callToActions: event?.callToActions ?? [],
    _dirty: false,
    _errors: {},
  };
}

interface EventTemplateFormPropsBase {
  brand: Brand;
  property: Property;
  mode: 'readonly' | 'filter' | 'edit';
  edit?: BareEventTemplate;
  onClose?: () => void;
  actionsElem?: HTMLDivElement;
  onFormUpdated?: (form: EventTemplateFormData) => void;
  onFormSubmitted?: () => Promise<void>;
}

type EventTemplateFormProps = EventTemplateFormPropsBase;

export interface EventTemplateFormRef {
  reset(): void;
}

export default forwardRef<EventTemplateFormRef, EventTemplateFormProps>((props, ref) => {
  const { brand, property, mode, edit, onClose, actionsElem, onFormUpdated, onFormSubmitted } =
    props;
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState<EventTemplateFormData>(DEFAULT_FORM_DATA(edit));

  const readonlyMode = mode === 'readonly';
  const filterMode = mode === 'filter';

  useEffect(() => {
    if (readonlyMode) resetForm();
  }, [edit]);

  useEffect(() => {
    if (onFormUpdated && formData._dirty) {
      onFormUpdated(formData);
    }
  }, [formData]);

  function resetForm() {
    const newFormData = DEFAULT_FORM_DATA(edit);
    setFormData(newFormData);
    if (onFormUpdated) onFormUpdated(newFormData);
  }

  function clearForm() {
    const newFormData = DEFAULT_FORM_DATA();
    setFormData(newFormData);
    if (onFormUpdated) onFormUpdated(newFormData);
  }

  useImperativeHandle(ref, () => ({
    reset: () => {
      resetForm();
    },
  }));

  function changeForm<T extends keyof EventTemplateFormDataBase>(
    key: T,
    val: EventTemplateFormDataBase[T]
  ) {
    setFormData({ ...formData, [key]: val, _dirty: true });
  }

  function handleCallToActionChange(callToActions: DailyProgramCta[]) {
    changeForm('callToActions', callToActions);
  }

  // CURRENTLY NOT BEING USED, BUT WILL BE USED WHEN COMPONENT IS USED UNDER THE
  // EVENT LIBRARY CONTEXT
  async function submitForm() {
    setSaving(true);
    try {
      if (edit) {
        // user intends to edit an existing event template
      }

      if (onFormSubmitted) await onFormSubmitted();
      if (onClose) onClose();
    } finally {
      setSaving(false);
    }
  }

  const errored = Object.keys(formData._errors).length > 0;

  // const files: VenueRequestFile[] =
  //   edit?.images.map(lnk => ({
  //     _id: lnk,
  //     name: lastEl(lnk.split('/')),
  //     location: lnk,
  //   })) ?? [];

  const segmentsMap = mapBy(brand.segments, '_id');
  const levelsMap = mapBy(brand.levels, '_id');
  const attributesMap = mapBy(brand.attributes, '_id');
  const zonesDict = mapBy(property.zones, '_id');

  const formHtml = (
    <>
      {!readonlyMode && (
        <div className="text-right -my-5 relative">
          <BubbleButton variant="smoke" inverse onClick={clearForm} icon="eraser">
            Clear Form
          </BubbleButton>
        </div>
      )}
      <div className="grid grid-cols-3 gap-4">
        {!filterMode && (
          <BubbleFormGroup label="Images" className="col-span-3">
            <BubbleImageSelector
              maxItems={3}
              readOnly={readonlyMode}
              value={formData.images || []}
              onValue={images => changeForm('images', images)}
            />
          </BubbleFormGroup>
        )}
        <BubbleFormGroup
          label="Title"
          error={formData._errors.title}
          className="col-span-3"
          asterisk
        >
          <BubbleInput
            block
            readOnly={readonlyMode}
            type="text"
            value={formData.title}
            onValue={val => changeForm('title', val)}
          />
        </BubbleFormGroup>
        <BubbleFormGroup
          label="Description"
          error={formData._errors.description}
          className="col-span-3"
        >
          <BubbleTextArea
            block
            value={formData.description}
            onValue={val => changeForm('description', val)}
            readOnly={readonlyMode}
          />
        </BubbleFormGroup>

        {!filterMode && (
          <>
            <BubbleFormGroup
              label="Duration (Minutes)"
              error={formData._errors.defaultDuration}
              asterisk
            >
              <div
                className={`flex items-center ${!readonlyMode ? 'bg-white rounded shadow' : ''}`}
              >
                {!readonlyMode && (
                  <BubbleButton
                    icon="minus"
                    variant="white"
                    onClick={() =>
                      changeForm('defaultDuration', Math.max(0, formData.defaultDuration - 15))
                    }
                  />
                )}
                <BubbleInput
                  block
                  type="number"
                  appearance="bare"
                  inputClassName="stretched-link py-2 px-1"
                  value={formData.defaultDuration}
                  readOnly={readonlyMode}
                  onValue={val => changeForm('defaultDuration', val)}
                />
                {!readonlyMode && (
                  <BubbleButton
                    icon="plus"
                    variant="white"
                    onClick={() => changeForm('defaultDuration', formData.defaultDuration + 15)}
                  />
                )}
              </div>
            </BubbleFormGroup>
            <BubbleFormGroup
              label="Display Options"
              error={formData._errors.interactiveFeedDisplay}
              className="col-span-2"
            >
              <BubbleSelect
                block
                allowUndefined
                undefinedLabel="Any"
                labelExtractor={id => EVENT_DISPLAY_MAP[id].label}
                value={formData.interactiveFeedDisplay}
                readOnly={readonlyMode}
                options={EVENT_DISPLAY_TYPES}
                onValue={val => changeForm('interactiveFeedDisplay', val)}
              />
            </BubbleFormGroup>
          </>
        )}
        <BubbleFormGroup label="Segment" error={formData._errors.segment} className="col-span-3">
          <BubbleSelect
            block
            allowUndefined
            readOnly={readonlyMode}
            labelExtractor={id => segmentsMap[id].name}
            value={formData.segment}
            options={brand.segments.map(s => s._id)}
            onValue={val => changeForm('segment', val)}
          />
        </BubbleFormGroup>
        <BubbleFormGroup label="Levels" error={formData._errors.levels} className="col-span-3">
          <BubbleTypeaheadSimple
            readOnly={readonlyMode}
            labelExtractor={id => levelsMap[id].name}
            value={formData.levels}
            onValue={val => changeForm('levels', val)}
            menuClassName="max-h-80 overflow-y-auto"
            options={brand.levels.map(l => l._id)}
            placeholder="Search..."
          />
        </BubbleFormGroup>
        <BubbleFormGroup
          label="Attributes"
          error={formData._errors.attributes}
          className="col-span-3"
        >
          <BubbleTypeaheadSimple
            readOnly={readonlyMode}
            labelExtractor={id => attributesMap[id].name}
            value={formData.attributes}
            onValue={val => changeForm('attributes', val)}
            menuClassName="max-h-80 overflow-y-auto"
            options={brand.attributes.map(a => a._id)}
            placeholder="Search..."
          />
        </BubbleFormGroup>
        {!filterMode && (
          <>
            <div className="col-span-3">
              <BubbleFormGroup label="Miscellaneous" className="mt-3">
                <div className="grid grid-cols-1 -mt-1">
                  <div className="relative py-1">
                    <BubbleInput
                      type="checkbox"
                      readOnly={readonlyMode}
                      className="mr-2"
                      inputClassName="stretched-link bg-white"
                      checked={formData.isPrivate}
                      onValue={chk => {
                        changeForm('isPrivate', chk);
                      }}
                    />
                    <span>Private</span>
                  </div>
                  <div className="relative py-1">
                    <BubbleInput
                      type="checkbox"
                      readOnly={readonlyMode}
                      className="mr-2"
                      inputClassName="stretched-link bg-white"
                      checked={formData.notNative}
                      onValue={chk => {
                        changeForm('notNative', chk);
                      }}
                    />
                    <span>Foreign Language</span>
                  </div>
                  <div className="relative py-1">
                    <BubbleInput
                      type="checkbox"
                      readOnly={readonlyMode}
                      className="mr-2"
                      inputClassName="stretched-link bg-white"
                      checked={formData.inventoried}
                      onValue={chk => {
                        changeForm('inventoried', chk);
                      }}
                    />
                    <span>Inventoried</span>
                  </div>
                  {formData.inventoried && (
                    <BubbleFormGroup
                      label="Initial Inventory"
                      error={formData._errors.initialInventory}
                      className="mb-2 mt-1"
                    >
                      <BubbleInput
                        block
                        readOnly={readonlyMode}
                        type="number"
                        value={formData.initialInventory}
                        onValue={val => changeForm('initialInventory', val)}
                      />
                    </BubbleFormGroup>
                  )}
                  <div className="relative py-1">
                    <BubbleInput
                      type="checkbox"
                      readOnly={readonlyMode}
                      className="mr-2"
                      inputClassName="stretched-link bg-white"
                      checked={formData.priced}
                      onValue={chk => {
                        changeForm('priced', chk);
                      }}
                    />
                    <span>Priced</span>
                  </div>
                  {formData.priced && (
                    <BubbleFormGroup
                      label="Initial price"
                      error={formData._errors.initialPrice}
                      className="mb-2 mt-1"
                    >
                      <BubbleInput
                        block
                        readOnly={readonlyMode}
                        type="number"
                        value={formData.initialPrice}
                        onValue={val => changeForm('initialPrice', val)}
                      />
                    </BubbleFormGroup>
                  )}
                  <div className="relative py-1">
                    <BubbleInput
                      type="checkbox"
                      readOnly={readonlyMode}
                      className="mr-2"
                      inputClassName="stretched-link bg-white"
                      checked={formData.crewOnly}
                      onValue={chk => {
                        changeForm('crewOnly', chk);
                      }}
                    />
                    <span>Internal Only</span>
                  </div>
                  <div className="relative py-1">
                    <BubbleInput
                      type="checkbox"
                      readOnly={readonlyMode}
                      className="mr-2"
                      inputClassName="stretched-link bg-white"
                      checked={formData.simple}
                      onValue={chk => {
                        changeForm('simple', chk);
                      }}
                    />
                    <span>Simple Event</span>
                  </div>
                  <div className="relative py-1">
                    <BubbleInput
                      type="checkbox"
                      readOnly={readonlyMode}
                      className="mr-2"
                      inputClassName="stretched-link bg-white"
                      checked={formData.homecoming}
                      onValue={chk => {
                        changeForm('homecoming', chk);
                      }}
                    />
                    <span>Homecoming</span>
                  </div>
                  <div className="relative py-1">
                    <BubbleInput
                      type="checkbox"
                      readOnly={readonlyMode}
                      className="mr-2"
                      inputClassName="stretched-link bg-white"
                      checked={formData.allAboard}
                      onValue={chk => {
                        changeForm('allAboard', chk);
                      }}
                    />
                    <span>All Aboard</span>
                  </div>
                  <div className="relative py-1">
                    <BubbleInput
                      type="checkbox"
                      readOnly={readonlyMode}
                      className="mr-2"
                      inputClassName="stretched-link bg-white"
                      checked={formData.vip}
                      onValue={chk => {
                        changeForm('vip', chk);
                      }}
                    />
                    <span>VIP</span>
                  </div>
                </div>
              </BubbleFormGroup>
            </div>
          </>
        )}

        {!filterMode && (
          <div className="col-span-3">
            <BubbleFormGroup label="Call to Actions">
              <CallToActions
                readOnly={readonlyMode}
                callToActions={formData.callToActions}
                onUpdate={handleCallToActionChange}
              />
            </BubbleFormGroup>
          </div>
        )}

        <div className="col-span-3 flex space-x-4">
          {!filterMode ? (
            <>
              <BubbleFormGroup label="Endpoints" className="flex-grow">
                <McmVisibilityTable
                  isTemplate
                  readOnly={readonlyMode}
                  sourceMcm={formData.mcm}
                  parentMcm={undefined}
                  onUpdateMcm={newMcm => changeForm('mcm', newMcm)}
                />
              </BubbleFormGroup>
              <BubbleFormGroup label="Audience" className="flex-grow">
                <AudienceVisibilityTable
                  isTemplate
                  readOnly={readonlyMode}
                  sourceAud={formData.audience}
                  parentAud={undefined}
                  onUpdateAudience={newAud => changeForm('audience', newAud)}
                />
              </BubbleFormGroup>
            </>
          ) : (
            <>
              <BubbleFormGroup label="Audience" className="flex-grow">
                <BubbleSelect
                  block
                  allowUndefined
                  undefinedLabel="All"
                  labelExtractor={id => zonesDict[id].name}
                  value={formData.audienceZoneFilter}
                  options={property.zones.map(z => z._id)}
                  onValue={val => changeForm('audienceZoneFilter', val)}
                />
              </BubbleFormGroup>
            </>
          )}
        </div>
      </div>
    </>
  );

  const formActions = (
    <div className="flex items-center">
      <BubbleButton
        variant="navy"
        onClick={submitForm}
        disabled={!formData._dirty || errored || saving}
        icon="floppy-disk"
      >
        Save Changes
      </BubbleButton>
      <BubbleButton
        variant="navy"
        onClick={resetForm}
        inverse
        disabled={errored || saving}
        icon="rotate-left"
      >
        Reset
      </BubbleButton>
    </div>
  );

  const portalButtons = actionsElem ? createPortal(formActions, actionsElem) : <></>;

  return (
    <>
      {formHtml}
      {portalButtons}
    </>
  );
});
